import { PurchaseOrderProductLineItem } from '../../clients/quipli-service/purchase-order/purchase-order';
import { OrderSection } from '../order/order-section/order-section';
import { Typography } from '../../components/typography/typography';
import { EditableField, FieldCounter } from '../../components/field/field';
import { convertToMoney, dineroSum } from '../../util/util';
import React, { PropsWithChildren, ReactNode, useState } from 'react';
import classNames from 'classnames';
import { ProductType } from '../../clients/quipli-service/product/product';
import { Button } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { toast } from '../../util/toasts';
import { Currency } from 'dinero.js';
import { OrderSummary } from '../order/order-summary/order-summary';
import { Spinner } from '../../components/spinner/spinner';

export const PurchaseOrderLineItems = ({
  productLineItems,
  setProductLineItems,
  isEditing = true,
  isSubmitting = false,
  canUpdateOrder = true,
  allowEmptyProductItems = false,
  currency,
  onClickEdit,
  onClickCancel,
  onClickSubmit,
}: PropsWithChildren<{
  productLineItems: PurchaseOrderProductLineItem[];
  setProductLineItems: (arr: PurchaseOrderProductLineItem[]) => void;
  isEditing?: boolean;
  isSubmitting?: boolean;
  canUpdateOrder?: boolean;
  allowEmptyProductItems?: boolean;
  currency: Currency;
  onClickEdit?: () => void;
  onClickCancel?: () => void;
  onClickSubmit?: () => Promise<void>;
}>) => {
  return (
    <OrderSection
      header={
        <div className="flex justify-between items-center">
          <Typography type="text-md" weight="medium" className="text-grey-800">
            Line Items
          </Typography>

          {canUpdateOrder &&
            (!isEditing ? (
              <Button className="button button-secondary" onClick={onClickEdit}>
                <Typography type="text-sm" weight="semi-bold" className="text-grey-700 hidden md:inline">
                  Update Order
                </Typography>
                <Icon icon="Edit" />
              </Button>
            ) : (
              <div className="flex gap-3">
                <Button className="button button-secondary" onClick={onClickCancel} disabled={isSubmitting}>
                  <Typography type="text-sm" weight="medium" className="text-grey-700">
                    Cancel
                  </Typography>
                </Button>
                <Button className="button button-primary" onClick={() => !isSubmitting && onClickSubmit?.()}>
                  <Typography type="text-sm" weight="semi-bold" className="text-white">
                    Save Changes
                  </Typography>
                  {isSubmitting && <Spinner />}
                </Button>
              </div>
            ))}
        </div>
      }
    >
      <TableProducts
        items={productLineItems}
        setProductLineItems={setProductLineItems}
        isEditing={isEditing}
        currency={currency}
        canDelete={allowEmptyProductItems || productLineItems.length > 1}
      />

      <OrderSummary
        title="Order Summary"
        lineItems={[
          {
            title: 'Subtotal',
            value: dineroSum(
              productLineItems.map((l) => convertToMoney(l.unitPrice, currency).multiply(l.quantity)),
              currency,
            ),
          },
        ]}
        defaultCurrency={currency}
      />
    </OrderSection>
  );
};

const TableProducts = ({
  items,
  setProductLineItems,
  isEditing,
  currency,
  canDelete = true,
}: {
  items: PurchaseOrderProductLineItem[];
  setProductLineItems: (arr: PurchaseOrderProductLineItem[]) => void;
  isEditing: boolean;
  currency: Currency;
  canDelete?: boolean;
}) => {
  const updateItem = (updatedItem: PurchaseOrderProductLineItem) => {
    setProductLineItems(items.map((item) => (updatedItem.id === item.id ? updatedItem : item)));
  };

  const removeItem = (id: string) => {
    setProductLineItems(items.filter((item) => item.id !== id));
  };

  return (
    <>
      {items.length > 0 ? (
        items.map((item) => (
          <PurchaseOrderProductLineItemView
            item={item}
            isEditing={isEditing}
            canDelete={canDelete}
            currency={currency}
            onChange={updateItem}
            onRemove={removeItem}
          />
        ))
      ) : (
        <div className="my-[8px] py-2 border border-solid border-grey-200 rounded-lg">
          <Typography type="text-sm" className="text-center">
            No items to display.
          </Typography>
        </div>
      )}
    </>
  );
};

const PurchaseOrderProductLineItemView = ({
  item,
  isEditing,
  canDelete,
  currency,
  onChange,
  onRemove,
}: {
  item: PurchaseOrderProductLineItem;
  isEditing: boolean;
  canDelete: boolean;
  currency: Currency;
  onChange: (i: PurchaseOrderProductLineItem) => void;
  onRemove: (id: string) => void;
}) => {
  const [modal, setModal] = useState<ReactNode>();
  const money = convertToMoney(item.unitPrice, currency);

  const ExtraInfo = (): JSX.Element => {
    switch (item.product.type) {
      case ProductType.Part: {
        return (
          <Typography type="text-sm" weight="regular">
            <span className="font-medium">Part Number:</span> {item.product.sku}
          </Typography>
        );
      }
      case ProductType.Custom: {
        return (
          <Typography type="text-sm" weight="regular">
            {item.description}
          </Typography>
        );
      }
    }

    return <></>;
  };
  return (
    <div className="overflow-x-auto min-w-[0] my-[8px] border border-solid border-grey-200 rounded-lg flex flex-nowrap">
      <div className="flex-[0_1_25%] flex flex-col">
        <LineItemHeader title="Name" />
        <LineItemField className="min-w-[250px] flex-col !items-start gap-2">
          <Typography type="text-sm" weight="regular">
            {item.product.type === ProductType.Custom ? item.name : item.product.name}
          </Typography>

          {<ExtraInfo />}
        </LineItemField>
      </div>
      <div className="flex-[0_1_25%] flex flex-col">
        <LineItemHeader title="Unit Price" />
        <LineItemField>
          {isEditing ? (
            <EditableField
              type="number"
              label="Unit Price"
              value={item.unitPrice}
              formattedValue={money.toFormat()}
              onChange={(value) => onChange({ ...item, unitPrice: value })}
            />
          ) : (
            <Typography type="text-sm" weight="regular">
              {money.toFormat()}
            </Typography>
          )}
        </LineItemField>
      </div>
      <div className="flex-[0_1_25%] flex flex-col">
        <LineItemHeader title="Quantity" />
        <LineItemField>
          {isEditing ? (
            <FieldCounter
              value={item.quantity}
              onChange={(newQty) => onChange({ ...item, quantity: newQty })}
              min={1}
            />
          ) : (
            <Typography type="text-sm" weight="regular">
              {item.quantity}
            </Typography>
          )}
        </LineItemField>
      </div>
      <div className="flex-[0_1_25%] flex flex-col">
        <LineItemHeader title="Total" />
        <LineItemField>
          <Typography type="text-sm" weight="regular">
            {money.multiply(item.quantity).toFormat()}
          </Typography>
        </LineItemField>
      </div>
      <div>
        <LineItemHeader title="" />
        <LineItemField>
          {isEditing && (
            <Button
              className="button-tertiary !py-0"
              onClick={() =>
                setModal(
                  <ConfirmModal
                    prompt="Remove item?"
                    additionalText="This cannot be undone"
                    noLabel="Cancel"
                    yesLabel="Remove"
                    yesDataCy="delete-cell-confirm"
                    yesButtonYellow
                    isOpen
                    headerIcon={
                      <div className="flex justify-center">
                        <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                      </div>
                    }
                    onConfirm={() => {
                      if (canDelete) {
                        onRemove(item.id);
                        toast('Product removed.', 'success');
                      } else {
                        toast('At least one item is required.', 'error');
                      }
                      setModal(undefined);
                    }}
                    close={() => setModal(undefined)}
                  />,
                )
              }
              dataCy="delete-cell-button"
            >
              <Icon icon="Trash" stroke="var(--grey-500)" />
            </Button>
          )}
        </LineItemField>
      </div>

      {modal}
    </div>
  );
};

const LineItemHeader = ({ title }: { title: string }) => {
  return (
    <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-grey-200">
      <Typography type="text-sm" weight="regular" className="text-grey-500">
        {title}
      </Typography>
    </div>
  );
};

const LineItemField = ({ children, className }: PropsWithChildren<{ className?: string }>) => {
  return <div className={classNames('min-h-[70px] py-[12px] px-[8px] flex items-center', className)}>{children}</div>;
};
